import {
  ArTranType,
  ClientSmartStatementRecipient,
  ContactEmailSmartStatementRecipient,
  ContactSmartStatementRecipient,
  GraphQl,
  SmartStatementRecipient,
  StatementStatus,
} from '@anduin/api';
import { isEmpty } from '@anduin/shared';

export type StatementRecipientModel = GraphQl.ClientCadenceType | SmartStatementRecipient;

export enum StatementRecipientType {
  CLIENT = 'CLIENT', // can be either v1 or v2
  CLIENT_V1 = 'CLIENT_V1',
  CLIENT_V2 = 'CLIENT_V2',
  CONTACT = 'CONTACT', // can either be billing contact or contact email
  BILLING_CONTACT = 'BILLING_CONTACT',
  CONTACT_EMAIL = 'CONTACT_EMAIL',
}

export abstract class AbstractStatementRecipient<Type extends StatementRecipientModel = StatementRecipientModel> {
  protected _model: Type;
  readonly status: StatementStatus;

  protected constructor(model: Type, shouldHideUntriggeredInvoices: boolean) {
    this._model = model;
    this.status = this.getStatus(shouldHideUntriggeredInvoices);
  }

  static create<
    Type extends StatementRecipientModel = StatementRecipientModel,
    Class extends StatementRecipientModelToClass<Type> = StatementRecipientModelToClass<Type>,
  >(model: Type, shouldHideUntriggeredInvoices: boolean): Class {
    if ('client_id' in model) {
      return new ClientStatementRecipientV1(model, shouldHideUntriggeredInvoices) as Class;
    } else if ('client' in model) {
      return new ClientStatementRecipient(model, shouldHideUntriggeredInvoices) as Class;
    } else if ('contactName' in model) {
      return new BillingContactStatementRecipient(model, shouldHideUntriggeredInvoices) as Class;
    } else if ('contacts' in model) {
      return new ContactEmailStatementRecipient(model, shouldHideUntriggeredInvoices) as Class;
    } else {
      throw new Error('Unknown statement recipient type');
    }
  }

  abstract get id(): number;
  abstract get name(): string;
  abstract get enabled(): boolean;
  abstract get totalOutstanding(): number;
  abstract get lastStatementDate(): string;
  abstract get hasTriggeredInvoices(): boolean;

  get model(): Type {
    return this._model;
  }

  // could make this mapped from `Type` class generic
  protected abstract get type(): StatementRecipientType;
  isType<InnerType extends StatementRecipientType>(type: InnerType): this is StatementRecipientTypeToClass<InnerType> {
    if (type === StatementRecipientType.CLIENT) {
      return this.type === StatementRecipientType.CLIENT_V1 || this.type === StatementRecipientType.CLIENT_V2;
    }

    if (type === StatementRecipientType.CONTACT) {
      return this.type === StatementRecipientType.BILLING_CONTACT || this.type === StatementRecipientType.CONTACT_EMAIL;
    }

    return this.type === type;
  }

  getStatus(shouldHideUntriggeredInvoices: boolean): StatementStatus {
    if (shouldHideUntriggeredInvoices && !this.hasTriggeredInvoices) {
      return undefined;
    }

    // get status from each respective model (they just happen to match here)
    return this._model.status as StatementStatus;
  }

  get currencyCode(): string {
    return 'USD';
  }

  get currencySymbol(): string {
    return '$';
  }
}

export class ClientStatementRecipientV1 extends AbstractStatementRecipient<GraphQl.ClientCadenceType> {
  constructor(model: GraphQl.ClientCadenceType, shouldHideUntriggeredInvoices: boolean) {
    super(model, shouldHideUntriggeredInvoices);
  }

  get type(): StatementRecipientType {
    return StatementRecipientType.CLIENT_V1;
  }

  get id(): number {
    return this._model.client_id;
  }

  get entityId(): number {
    return this._model.client_id;
  }

  get ref(): string {
    return this._model.client_ref;
  }

  get name(): string {
    return this._model.client_name;
  }

  get enabled(): boolean {
    return !this._model.exclude_from_collections;
  }

  get totalOutstanding(): number {
    return this._model.total_outstanding_amount_in_currency || this._model.total_outstanding_amount;
  }

  get lastStatementDate(): string {
    return this._model.last_statement_date;
  }

  get hasTriggeredInvoices(): boolean {
    return this._model.has_triggered_invoices;
  }

  get isAutopayActive(): boolean {
    return this._model.is_currently_autopay;
  }

  get partnerName(): string {
    return this._model.partner_name;
  }

  get currencyCode(): string {
    return this._model.currency_code ?? super.currencyCode;
  }

  get currencySymbol(): string {
    return this._model.currency_symbol ?? super.currencySymbol;
  }
}

abstract class BaseStatementRecipientV2<
  Type extends SmartStatementRecipient = SmartStatementRecipient,
> extends AbstractStatementRecipient<Type> {
  protected constructor(model: Type, shouldHideUntriggeredInvoices: boolean) {
    super(model, shouldHideUntriggeredInvoices);
  }

  get id(): number {
    return this._model.id;
  }

  get enabled(): boolean {
    return this._model.enabled;
  }

  get totalOutstanding(): number {
    return this._model.totalOutstanding;
  }

  get lastStatementDate(): string {
    return this._model.lastStatementDate;
  }

  get hasTriggeredInvoices(): boolean {
    return true; // in v2, a statement will only be staged if it has triggered invoices
  }
}

export class ClientStatementRecipient extends BaseStatementRecipientV2<ClientSmartStatementRecipient> {
  constructor(model: ClientSmartStatementRecipient, shouldHideUntriggeredInvoices: boolean) {
    super(model, shouldHideUntriggeredInvoices);
  }

  get type(): StatementRecipientType {
    return StatementRecipientType.CLIENT_V2;
  }

  get entityId(): number {
    return this._model.client.id;
  }

  get name(): string {
    return this._model.client.name;
  }

  get ref(): string {
    return this._model.client.clientRef;
  }

  get isAutopayActive(): boolean {
    return this._model.isAutoPayActive;
  }

  get partnerName(): string {
    return this._model.partnerName;
  }
}

export abstract class BaseContactStatementRecipient<
  Type extends ContactSmartStatementRecipient | ContactEmailSmartStatementRecipient,
> extends BaseStatementRecipientV2<Type> {
  protected constructor(model: Type, shouldHideUntriggeredInvoices: boolean) {
    super(model, shouldHideUntriggeredInvoices);
  }

  get clientNames(): string[] {
    return this._model.clientNames;
  }
}

export class BillingContactStatementRecipient extends BaseContactStatementRecipient<ContactSmartStatementRecipient> {
  constructor(model: ContactSmartStatementRecipient, shouldHideUntriggeredInvoices: boolean) {
    super(model, shouldHideUntriggeredInvoices);
  }

  get entityId(): number {
    return 0; // TODO: Implement
  }

  get type(): StatementRecipientType {
    return StatementRecipientType.BILLING_CONTACT;
  }

  get name(): string {
    return this._model.contactName;
  }
}

export class ContactEmailStatementRecipient extends BaseContactStatementRecipient<ContactEmailSmartStatementRecipient> {
  constructor(model: ContactEmailSmartStatementRecipient, shouldHideUntriggeredInvoices: boolean) {
    super(model, shouldHideUntriggeredInvoices);
  }

  get type(): StatementRecipientType {
    return StatementRecipientType.CONTACT_EMAIL;
  }

  get name(): string {
    return this._model.email;
  }

  get contactNames(): string[] {
    return this._model.contacts.map(({ name }) => name);
  }
}

export type StatementRecipient =
  | ClientStatementRecipientV1
  | ClientStatementRecipient
  | BillingContactStatementRecipient
  | ContactEmailStatementRecipient;

type StatementRecipientModelToClass<Type extends StatementRecipientModel> = Type extends GraphQl.ClientCadenceType
  ? ClientStatementRecipientV1
  : Type extends ClientSmartStatementRecipient
    ? ClientStatementRecipient
    : Type extends ContactSmartStatementRecipient
      ? BillingContactStatementRecipient
      : Type extends ContactEmailSmartStatementRecipient
        ? ContactEmailStatementRecipient
        : never;

type StatementRecipientTypeToClass<Type extends StatementRecipientType> = Type extends StatementRecipientType.CLIENT
  ? ClientStatementRecipientV1 | ClientStatementRecipient
  : Type extends StatementRecipientType.CLIENT_V1
    ? ClientStatementRecipientV1
    : Type extends StatementRecipientType.CLIENT_V2
      ? ClientStatementRecipient
      : Type extends StatementRecipientType.CONTACT
        ? BillingContactStatementRecipient | ContactEmailStatementRecipient
        : Type extends StatementRecipientType.CONTACT_EMAIL
          ? ContactEmailStatementRecipient
          : Type extends StatementRecipientType.BILLING_CONTACT
            ? BillingContactStatementRecipient
            : never;
